import React, { useState, useEffect, useRef } from "react";
import { FaChevronDown, FaEnvelope, FaTimes, FaSave, FaArrowRight } from "react-icons/fa"
import { RiFlowChart } from "react-icons/ri"
import { MdContentPaste } from "react-icons/md"
import { IoSettingsOutline } from "react-icons/io5"
import { Badge } from "react-bootstrap"
import Ckeditor from "../../common/ckeditor"
import axios from "axios"
import { localserver } from "../../../api/common"
import { showToast, ToastMessage } from "../../Shared/ToastMessage"
import "./email-components.css"

// Constant declarations before state to avoid TDZ issues
const emailTypeMap = {
  1: "attendees",
  2: "orders",
  3: "prepaidOrders",
  4: "reminders",
}

const targetMap = {
  1: "Individual",
  2: "All Tickets",
  3: "Accompanying Person",
}

const targets = ["All Tickets", "Individual", "Accompanying Person"]

const options = ["On successful registration", "On order completion", "On prepaid order", "On reminder schedule"]

const dynamicTags = [
  "Event.Logo",
  "Event.Title",
  "Event.Date",
  "Add.To.Calendar",
  "Order.id",
  "Attendee.Name",
  "Registration.Code",
  "Attendee.Email",
  "MTicket.Link",
  "Attendee.AddOns",
  "QR.Code",
]

const NewEmailModal = ({ show, handleClose, emailData = {}, getValidation }) => {
  // State initialization
  const [isModal, setModal] = useState("")
  const [isSearchbar, toggleSearchBar] = useState(false)
  const [activeTab, setActiveTab] = useState("flow")
  const [emailName, setEmailName] = useState("")
  const [selectedOrderType, setSelectedOrderType] = useState("On successful registration")
  const [showTargetDropdown, setShowTargetDropdown] = useState(false)
  const [selectedTarget, setSelectedTarget] = useState("Individual")
  const [modalClass, setModalClass] = useState("")
  const [editorData, setEditorData] = useState("<p>{{Event.Date}}</p><p>{{Event.Title}}</p>")
  const [eventId, setEventId] = useState(localStorage.getItem("eventId") || "")
  const [emailType, setEmailType] = useState("attendees")
  const [targetArray, setTargetArray] = useState("")
  const [scheduleDate, setScheduleDate] = useState("")
  const [subjectLine, setSubjectLine] = useState("")
  const [senderEmail, setSenderEmail] = useState("certificates@amasi.org")
  const [senderName, setSenderName] = useState("")
  const [replyToEmail, setReplyToEmail] = useState("")
  const [ccEmails, setCcEmails] = useState("")
  const [editorError, setEditorError] = useState(null)

  const targetRef = useRef(null)
  const targetTriggerRef = useRef(null)
  const editorRef = useRef(null)
  const testEmailInputRef = useRef(null)

  // Update state when emailData changes
  useEffect(() => {
    if (emailData) {
      setEmailName(emailData.title || "")
      setSelectedOrderType(emailData.trigger_function || "On successful registration")
      setSelectedTarget(emailData.target ? targetMap[emailData.target] || "Individual" : "Individual")
      setEditorData(emailData.email_content || "<p>{{Event.Date}}</p><p>{{Event.Title}}</p>")
      setEventId(emailData.event_id || localStorage.getItem("eventId") || "")
      setEmailType(emailData.email_type ? emailTypeMap[emailData.email_type] || "attendees" : "attendees")
      setTargetArray(emailData.target_array || "")
      setScheduleDate(emailData.schedule_date || "")
      setSubjectLine(emailData.subject_line || "")
      setSenderEmail(emailData.sender_email || "certificates@amasi.org")
      setSenderName(emailData.sender_name || "")
      setReplyToEmail(emailData.reply_to_email || "")
      setCcEmails(emailData.cc_emails || "")
      setEditorError(null)
    }
  }, [emailData])

  // Handle modal visibility animation
  useEffect(() => {
    if (show) {
      setModalClass("slide-in-right")
    } else {
      setModalClass("")
    }
  }, [show])

  // Handle click outside for target dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (targetRef.current && !targetRef.current.contains(event.target)) {
        setShowTargetDropdown(false)
      }
      if (targetTriggerRef.current && !targetTriggerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Wait for CKEditor to initialize
  useEffect(() => {
    if (activeTab === "content") {
      const timer = setTimeout(() => {
        if (editorRef.current && editorRef.current.editor) {
          try {
            editorRef.current.editor.focus()
            setEditorError(null)
          } catch (error) {
            console.error("CKEditor focus error:", error)
            setEditorError("Failed to focus CKEditor. Please try again.")
          }
        }
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [activeTab, show])

  // Map target name to ID for payload
  const getTargetId = (targetName) => {
    const targetEntry = Object.entries(targetMap).find(([_, value]) => value === targetName)
    return targetEntry ? Number.parseInt(targetEntry[0]) : 1
  }

  const handleTargetSelect = (target) => {
    setSelectedTarget(target)
    setShowTargetDropdown(false)
  }

  const toggleTargetDropdown = () => {
    setShowTargetDropdown((prev) => !prev)
  }

  const handleOrderTypeChange = (option) => {
    setSelectedOrderType(option)
    setIsOpen(false)
  }

  const [isOpen, setIsOpen] = useState(false)

  const handleTagClick = (tag) => {
    if (editorRef.current && editorRef.current.editor) {
      try {
        const editor = editorRef.current.editor
        editor.insertHtml(`{{${tag}}}`)
        editor.focus()
        setEditorError(null)
      } catch (error) {
        console.error("Error inserting dynamic tag:", error)
        setEditorError("Failed to insert dynamic tag. Please try again.")
      }
    } else {
      setEditorError("Editor is loading, please try again in a moment.")
    }
  }

  // Validate required fields for the current tab
  const validateTab = (tab) => {
    const errors = []
    if (tab === "flow") {
      if (!emailName.trim()) {
        errors.push("Email Title is required.")
      }
      if (!selectedOrderType) {
        errors.push("Trigger is required.")
      }
    } else if (tab === "content") {
      if (!subjectLine.trim()) {
        errors.push("Subject Line is required.")
      }
    } else if (tab === "settings") {
      if (!senderName.trim()) {
        errors.push("Sender Name is required.")
      }
    }
    return errors
  }

  // Handle Save & Continue
  const handleSave = async () => {
    const errors = validateTab(activeTab)
    if (errors.length > 0) {
      errors.forEach((error) => showToast(error, "error"))
      return
    }

    if (activeTab === "settings") {
      const allErrors = [...validateTab("flow"), ...validateTab("content"), ...validateTab("settings")]
      if (allErrors.length > 0) {
        allErrors.forEach((error) => showToast(error, "error"))
        return
      }

      const emailTypeId = Object.keys(emailTypeMap).find((key) => emailTypeMap[key] === emailType) || "1"
      const payload = {
        event_id: eventId,
        email_type: Number.parseInt(emailTypeId, 10),
        title: emailName,
        trigger_function: selectedOrderType,
        target: getTargetId(selectedTarget),
        target_array: emailType === "prepaidOrders" ? targetArray : "",
        schedule_date: scheduleDate || "",
        subject_line: subjectLine,
        email_content: editorData,
        sender_email: senderEmail,
        sender_name: senderName,
        reply_to_email: replyToEmail,
        cc_emails: ccEmails,
      }

      if (emailData.id) {
        payload.id = emailData.id
      }

      try {
        const response = await axios({
          method: "POST",
          url: `${localserver}/createEmail_template`,
          data: payload,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        })

        if (response.data.success) {
          showToast(response.data.message, "success")
          getValidation()
          handleClose()
        } else {
          showToast("Failed to save email template.", "error")
        }
      } catch (error) {
        console.error("Error saving email template:", error)
        showToast("Failed to save email template. Please try again.", "error")
      }
    } else {
      if (activeTab === "flow") {
        setActiveTab("content")
      } else if (activeTab === "content") {
        setActiveTab("settings")
      }
    }
  }

  return (
    <>
      <ToastMessage />
      <div>
        {show && (
          <>
            {/* Modal Backdrop */}
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.4)",
                backdropFilter: "blur(2px)",
                zIndex: 1040,
                animation: "fadeIn 0.2s ease-out",
              }}
              onClick={() => {
                setActiveTab("flow")
                handleClose()
              }}
            />

            <div
              className={`extraspace_search_div1 email_page add_attendites_model search_div email_div open ${modalClass}`}
              style={{
                display: "block",
                position: "fixed",
                right: 0,
                top: 0,
                height: "100vh",

                zIndex: 1050,
                overflowY: "auto",
                boxShadow: "-2px 0 16px rgba(0, 0, 0, 0.1)",
                animation: "slideInRight 0.3s ease-out",
              }}
            >
              <div
                className="card"
                style={{
                  minHeight: "100vh",
                  margin: 0,
                  borderRadius: 0,
                  border: "none",
                  background: "#ffffff",
                }}
              >
                {/* Simple Header */}
                <div className="common_sidebar_header" >
                  <div className="d-flex align-items-center gap-2">
                    <h5 >
                      {emailData.id ? "Edit Automated Email" : "New Automated Email"}
                    </h5>
                  </div>
                  <button
                    onClick={() => {
                      setActiveTab("flow")
                      handleClose()
                    }}
                    className="close-notification-btn">
                    <FaTimes />
                  </button>
                </div>

                <div className="new_email_tab"  >
                  {/* Simple Tab Navigation */}
                  <div className="small-tab-wrapper">
                    {[
                      { key: "flow", icon: RiFlowChart, label: "Flow" },
                      { key: "content", icon: MdContentPaste, label: "Content" },
                      { key: "settings", icon: IoSettingsOutline, label: "Settings" },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        className={`small-tab-btn ${activeTab === tab.key ? "active" : ""}`}
                        onClick={() =>{
                          const errors = validateTab(activeTab)
                          if (errors.length > 0) {
                            errors.forEach((error) => showToast(error, "error"))
                            return
                          }else{
                            setActiveTab(tab.key)
                          }              
                          
                        }}
                      >
                        <tab.icon className="small-tab-icon" />
                        {tab.label}
                      </button>
                    ))}
                  </div>


                  {/* Tab Content */}
                  {activeTab === "flow" && (
                    <div>
                      <div className="row g-3">
                        <div className="col-md-12 mb-3">
                          <label
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#495057",
                              marginBottom: "6px",
                              display: "block",
                            }}
                          >
                            Email Title <span style={{ color: "#dc3545" }}>*</span>
                          </label>
                          <input
                            type="text"
                            value={emailName}
                            onChange={(e) => setEmailName(e.target.value)}
                            className="form-control"
                            placeholder="Enter email title"
                            style={{
                              padding: "10px 12px",
                              fontSize: "14px",
                              border: "1px solid #dee2e6",
                              borderRadius: "6px",
                            }}
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <div className="position-relative" ref={targetTriggerRef}>
                            <label
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#495057",
                                marginBottom: "6px",
                                display: "block",
                              }}
                            >
                              Trigger <span style={{ color: "#dc3545" }}>*</span>
                            </label>
                            <div
                              onClick={() => setIsOpen(!isOpen)}
                              style={{
                                padding: "10px 12px",
                                fontSize: "14px",
                                border: "1px solid #dee2e6",
                                borderRadius: "6px",
                                background: "white",
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span>{selectedOrderType}</span>
                              <FaChevronDown
                                style={{
                                  fontSize: "12px",
                                  color: "#6c757d",
                                  transition: "transform 0.2s ease",
                                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                }}
                              />
                            </div>
                            {isOpen && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  marginTop: "4px",
                                  background: "white",
                                  border: "1px solid #dee2e6",
                                  borderRadius: "6px",
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                                  zIndex: 1000,
                                  overflow: "hidden",
                                }}
                              >
                                {options.map((option, index) => (
                                  <div
                                    key={index}
                                    onClick={() => handleOrderTypeChange(option)}
                                    style={{
                                      padding: "10px 12px",
                                      fontSize: "14px",
                                      cursor: "pointer",
                                      transition: "background 0.2s ease",
                                      borderBottom: index < options.length - 1 ? "1px solid #f1f3f5" : "none",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = "#f8f9fa"
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = "white"
                                    }}
                                  >
                                    {option}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-md-6 mb-3">
                          <div className="position-relative" ref={targetRef}>
                            <label
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#495057",
                                marginBottom: "6px",
                                display: "block",
                              }}
                            >
                              To
                            </label>
                            <div
                              onClick={toggleTargetDropdown}
                              style={{
                                padding: "10px 12px",
                                fontSize: "14px",
                                border: "1px solid #dee2e6",
                                borderRadius: "6px",
                                background: "white",
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span>{selectedTarget}</span>
                              <FaChevronDown
                                style={{
                                  fontSize: "12px",
                                  color: "#6c757d",
                                  transition: "transform 0.2s ease",
                                  transform: showTargetDropdown ? "rotate(180deg)" : "rotate(0deg)",
                                }}
                              />
                            </div>
                            {showTargetDropdown && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  marginTop: "4px",
                                  background: "white",
                                  border: "1px solid #dee2e6",
                                  borderRadius: "6px",
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                                  zIndex: 1000,
                                  overflow: "hidden",
                                }}
                              >
                                {targets.map((target, index) => (
                                  <div
                                    key={index}
                                    onClick={() => handleTargetSelect(target)}
                                    style={{
                                      padding: "10px 12px",
                                      fontSize: "14px",
                                      cursor: "pointer",
                                      transition: "background 0.2s ease",
                                      borderBottom: index < targets.length - 1 ? "1px solid #f1f3f5" : "none",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = "#f8f9fa"
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = "white"
                                    }}
                                  >
                                    {target}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {emailType === "prepaidOrders" && (
                          <div className="col-md-12">
                            <label
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#495057",
                                marginBottom: "6px",
                                display: "block",
                              }}
                            >
                              Target Array (Prepaid Orders)
                            </label>
                            <input
                              type="text"
                              value={targetArray}
                              onChange={(e) => setTargetArray(e.target.value)}
                              className="form-control"
                              placeholder="Enter target array data"
                              style={{
                                padding: "10px 12px",
                                fontSize: "14px",
                                border: "1px solid #dee2e6",
                                borderRadius: "6px",
                              }}
                            />
                          </div>
                        )}
                      </div>

                      <div
                        style={{
                          marginTop: "20px",
                          paddingTop: "16px",
                          borderTop: "1px solid #e9ecef",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          onClick={handleSave}
                          style={{
                            padding: "10px 24px",
                            fontSize: "14px",
                            fontWeight: "600",
                            borderRadius: "6px",
                            background: "#184778",
                            border: "none",
                            color: "white",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#0d3352"
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#184778"
                          }}
                        >
                          Continue <FaArrowRight style={{ fontSize: "12px" }} />
                        </button>
                      </div>
                    </div>
                  )}

                  {activeTab === "content" && (
                    <div>
                      {/* Subject Line */}
                      <div style={{ marginBottom: "16px" }}>
                        <label
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#495057",
                            marginBottom: "6px",
                            display: "block",
                          }}
                        >
                          Subject Line <span style={{ color: "#dc3545" }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={subjectLine}
                          onChange={(e) => setSubjectLine(e.target.value)}
                          className="form-control"
                          placeholder="Enter subject line"
                          style={{
                            padding: "10px 12px",
                            fontSize: "14px",
                            border: "1px solid #dee2e6",
                            borderRadius: "6px",
                          }}
                        />
                      </div>

                      {/* Dynamic Tags - Above Editor */}
                      <div
                        style={{
                          marginBottom: "16px",
                          padding: "12px",
                          background: "#f8f9fa",
                          borderRadius: "6px",
                          border: "1px solid #e9ecef",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#495057",
                            marginBottom: "8px",
                          }}
                        >
                          Dynamic Tags
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                          }}
                        >
                          {dynamicTags.map((tag) => (
                            <Badge
                              key={tag}
                              onClick={() => handleTagClick(tag)}
                              style={{
                                // cursor: "pointer",
                                // padding: "6px 10px",
                                // fontSize: "12px",
                                // fontWeight: "500",
                                // background: "white",
                                // color: "#495057",
                                // border: "1px solid #dee2e6",
                                // borderRadius: "4px",
                                // transition: "all 0.2s ease",
                              }}
                            // onMouseEnter={(e) => {
                            //   e.currentTarget.style.background = "#184778"
                            //   e.currentTarget.style.color = "white"
                            //   e.currentTarget.style.borderColor = "#184778"
                            // }}
                            // onMouseLeave={(e) => {
                            //   e.currentTarget.style.background = "white"
                            //   e.currentTarget.style.color = "#495057"
                            //   e.currentTarget.style.borderColor = "#dee2e6"
                            // }}
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6c757d",
                            marginTop: "8px",
                          }}
                        >
                          Click any tag to insert it into the email content
                        </div>
                      </div>

                      {editorError && (
                        <div
                          style={{
                            padding: "10px 12px",
                            background: "#fff3cd",
                            border: "1px solid #ffc107",
                            borderRadius: "6px",
                            fontSize: "13px",
                            color: "#856404",
                            marginBottom: "16px",
                          }}
                        >
                          {editorError}
                        </div>
                      )}

                      {/* Full Width Editor */}
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "6px",
                          overflow: "hidden",
                        }}
                      >
                        <Ckeditor
                          key={`editor-${show}-${activeTab}`}
                          activeClass="summernote"
                          ref={editorRef}
                          data={editorData}
                          onChange={(data) => {
                            setEditorData(data)
                            setEditorError(null)
                          }}
                        />
                      </div>

                      <div
                        style={{
                          marginTop: "20px",
                          paddingTop: "16px",
                          borderTop: "1px solid #e9ecef",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          onClick={handleSave}
                          style={{
                            padding: "10px 24px",
                            fontSize: "14px",
                            fontWeight: "600",
                            borderRadius: "6px",
                            background: "#184778",
                            border: "none",
                            color: "white",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#0d3352"
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#184778"
                          }}
                        >
                          Continue <FaArrowRight style={{ fontSize: "12px" }} />
                        </button>
                      </div>
                    </div>
                  )}

                  {activeTab === "settings" && (
                    <div>
                      <div
                        style={{
                          marginBottom: "20px",
                          padding: "12px",
                          background: "#f8f9fa",
                          borderRadius: "6px",
                          border: "1px solid #e9ecef",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#212529",
                            marginBottom: "4px",
                          }}
                        >
                          Email Settings
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#6c757d",
                          }}
                        >
                          Configure sender information and delivery settings
                        </div>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-6 mb-3">
                          <label
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#495057",
                              marginBottom: "6px",
                              display: "block",
                            }}
                          >
                            Sender Email
                          </label>
                          <input
                            type="email"
                            disabled
                            value={senderEmail}
                            style={{
                              padding: "10px 12px",
                              fontSize: "14px",
                              border: "1px solid #dee2e6",
                              borderRadius: "6px",
                              background: "#f8f9fa",
                              color: "#6c757d",
                              width: "100%",
                            }}
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#495057",
                              marginBottom: "6px",
                              display: "block",
                            }}
                          >
                            Sender Name <span style={{ color: "#dc3545" }}>*</span>
                          </label>
                          <input
                            type="text"
                            value={senderName}
                            onChange={(e) => setSenderName(e.target.value)}
                            placeholder="Enter sender name"
                            style={{
                              padding: "10px 12px",
                              fontSize: "14px",
                              border: "1px solid #dee2e6",
                              borderRadius: "6px",
                              width: "100%",
                            }}
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#495057",
                              marginBottom: "6px",
                              display: "block",
                            }}
                          >
                            Reply-to Email <span style={{ color: "#6c757d", fontWeight: "400" }}>(optional)</span>
                          </label>
                          <input
                            type="email"
                            value={replyToEmail}
                            onChange={(e) => setReplyToEmail(e.target.value)}
                            placeholder="Enter reply-to email"
                            style={{
                              padding: "10px 12px",
                              fontSize: "14px",
                              border: "1px solid #dee2e6",
                              borderRadius: "6px",
                              width: "100%",
                            }}
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#495057",
                              marginBottom: "6px",
                              display: "block",
                            }}
                          >
                            CC Emails <span style={{ color: "#6c757d", fontWeight: "400" }}>(optional)</span>
                          </label>
                          <input
                            type="text"
                            value={ccEmails}
                            onChange={(e) => setCcEmails(e.target.value)}
                            placeholder="Comma-separated emails"
                            style={{
                              padding: "10px 12px",
                              fontSize: "14px",
                              border: "1px solid #dee2e6",
                              borderRadius: "6px",
                              width: "100%",
                            }}
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#495057",
                              marginBottom: "6px",
                              display: "block",
                            }}
                          >
                            Schedule Date <span style={{ color: "#6c757d", fontWeight: "400" }}>(optional)</span>
                          </label>
                          <input
                            type="datetime-local"
                            value={scheduleDate}
                            onChange={(e) => setScheduleDate(e.target.value)}
                            style={{
                              padding: "10px 12px",
                              fontSize: "14px",
                              border: "1px solid #dee2e6",
                              borderRadius: "6px",
                              width: "100%",
                            }}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: "20px",
                          paddingTop: "16px",
                          borderTop: "1px solid #e9ecef",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          onClick={handleSave}
                          className="btn btn-outline-primary "
                        >
                          <FaSave   /> Save Email Template
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  )
}

export default NewEmailModal
